import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TruncatedText } from '../../ui';
import { SupplierThread } from './types';
import { validityCommitment } from '../../../hooks/outreachActionModel';
import { formatPrice } from '../../../utils/currency';

/**
 * The extracted quote, read-only, on ONE line: what the supplier committed to,
 * with the buyer's own target beside it for scale. Purely the ledger read-out
 * — the counter lives on the moves bar above the composer, so there is only one
 * path to a counter-offer draft.
 *
 * The two facts that are not numbers — how long the price stands and which
 * reply it came out of — sit behind the info affordance rather than eating a
 * second line of the conversation's height. Nothing is dropped; the validity is
 * still the supplier's own words.
 *
 * Only supplier-stated commitments appear here (quoteSource SUPPLIER_QUOTED);
 * scraped research prices never do.
 */
interface QuoteStripProps {
  thread: SupplierThread;
}

/**
 * One quoted number. The value clips rather than pushing the line apart — a
 * currency the platform formats long, or an MOQ stated as a sentence, still
 * reads in full on hover instead of disappearing off the strip's edge.
 */
const Fact: React.FC<{ label: string; value: string; unit?: string; tone?: 'default' | 'warn' }> = ({
  label,
  value,
  unit,
  tone = 'default',
}) => (
  <span className="inline-flex min-w-0 items-baseline gap-s2">
    <span className="shrink-0 text-[10px] text-ink-muted">{label}</span>
    <TruncatedText
      className={`max-w-[132px] font-mono text-[12px] font-semibold ${tone === 'warn' ? 'text-warn-ink' : 'text-ink'}`}
      text={value}
      side="bottom"
    />
    {unit && <span className="shrink-0 text-[10px] font-normal text-ink-muted">{unit}</span>}
  </span>
);

const Dot: React.FC = () => (
  <span aria-hidden="true" className="shrink-0 text-ink-muted">
    ·
  </span>
);

const QuoteStrip: React.FC<QuoteStripProps> = ({ thread }) => {
  const quote = thread.quote;
  if (!quote) return null;
  const validity = validityCommitment(thread);
  const quotedOn = quote.quotedAtIso
    ? new Date(quote.quotedAtIso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : undefined;

  return (
    <div className="flex shrink-0 items-center gap-s3 overflow-hidden border-b border-line-divider-soft bg-canvas px-[16px] py-[5px]">
      <Fact label="Their quote" value={formatPrice(quote.unitPrice, quote.currency)} unit="/unit" />
      {quote.leadTimeWeeks != null && (
        <>
          <Dot />
          <Fact label="Lead" value={`${quote.leadTimeWeeks} wk`} tone="warn" />
        </>
      )}
      {quote.moq != null && (
        <>
          <Dot />
          <Fact label="MOQ" value={String(quote.moq)} />
        </>
      )}
      {thread.targetPrice != null && (
        <>
          <Dot />
          <Fact label="Your target" value={formatPrice(thread.targetPrice, quote.currency)} />
        </>
      )}
      {(validity || quotedOn) && (
        <Tooltip
          className="ml-auto shrink-0"
          content={
            <>
              {validity && <span className="block">{validity.statement}</span>}
              {quotedOn && (
                <span className={`block ${validity ? 'mt-s1 text-white/70' : ''}`}>From their {quotedOn} reply</span>
              )}
            </>
          }
        >
          <button
            type="button"
            aria-label="Where this quote came from, and how long it stands"
            className="flex h-[18px] w-[18px] items-center justify-center rounded-control text-ink-muted hover:bg-neutral-bg hover:text-ink-secondary"
          >
            <Info className="h-[13px] w-[13px]" />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default QuoteStrip;
